<template>
  <template v-if="certificates !== null && !isLoading">
    <rbz-table
      :columns="columns"
      :table-data="certificates"
      :row-buttons="rowButtons"
      :row-classes="
        (item) => {
          return newCertificateId === item.id ? 'highlight-row' : '';
        }
      "
      :show-download-button="false"
      class="rbz-list-table"
      data-qa="client-ca-certificates-table"
      :show-filter-row="true"
      :new-button-label="$t('certificate.clientCaCertificate.newCertificate')"
      @new-button-clicked="openNewModal"
      @edit-row-button-clicked="openEditModal"
      @delete-row-button-clicked="openDeleteModal"
    >
      <template #tableResultsOverride="props">
        {{ $t('certificate.counter', props.numberOfResults) }}
      </template>
    </rbz-table>
    <div class="new-certificate-modal">
      <rbz-modal-dialog
        :show="showNewModal"
        :title="$t('certificate.clientCaCertificate.newModal.title')"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="addNewCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeNewModal"
        @closeModal="closeNewModal"
        size="medium"
      >
        <template #content>
          <div class="new-certificate-modal-notice-wrapper">
            <system-notice
              color="fun-blue"
              :text="$t('certificate.clientCaCertificate.issuerChain')"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name"
              variant="tertiary"
              class="certificate-name-input"
              data-qa="certificate-name-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="newCertificateData.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!newCertificateNameError || !!newCertificateDuplicateError"
              :error-message-text="newCertificateNameError || newCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <label class="rbz-label">
              {{ $t('certificate.certificateType') }}
            </label>
            <div class="certificate-type-wrapper">
              <rbz-form-radio
                id="certificate-type-manual"
                name="certificate-type"
                data-qa="certificate-type-manual-radio"
                :label="$t('certificate.radioManualInput')"
                @input="newModalTypeClear('manual')"
                :checked="newCertificateData.isManualInput === 'manual'"
              />
              <rbz-form-radio
                id="certificate-type-extraction-pem-der"
                name="certificate-type"
                data-qa="certificate-type-extract-pem-der-radio"
                :label="$t('certificate.radioExtractFile', { type: 'PEM/DER' })"
                @input="newModalTypeClear('extractPemDer')"
                :checked="newCertificateData.isManualInput === 'extractPemDer'"
              />
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'manual'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div v-if="newCertificateData.isManualInput === 'manual'">
            <span class="rbz-label">
              {{ $t('certificate.modalManualTitle') }}
            </span>
          </div>
          <div v-else>
            <span class="rbz-label">
              {{ $t('certificate.modalExtractionTitle') }}
            </span>
            <div class="rbz-field">
              <div class="file has-name">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    @input="
                      loadCertFile($event);
                      extractFile();
                    "
                    name="certFileInputPem"
                    data-qa="file-input-pem"
                    accept=".pem, .der"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <icon-upload />
                    </span>
                    <span class="file-label">
                      {{ $t('certificate.chooseFile', { type: 'PEM/DER' }) }}
                    </span>
                  </span>
                  <span class="file-name">
                    {{ newCertificateData.file?.name || $t('common.noFileChosen') }}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'extractPemDer'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              v-model:inputValue="newCertificateData.certificate"
              :placeholder="$t('certificate.certificateBodyPlaceholder')"
              rows="6"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
            />
          </div>
        </template>
      </rbz-modal-dialog>
    </div>
    <div class="edit-certificate-modal">
      <rbz-modal-dialog
        :show="showEditModal"
        :title="$t('certificate.clientCaCertificate.editModal.title')"
        :subtitle="selectedCertificate?.name"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="editCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeEditModal"
        :tertiary-btn-title="
          isCertDownloadEnabled ? $t('certificate.downloadFile', { type: 'PEM' }) : ''
        "
        :tertiary-btn-is-loading="isDownloadLoading"
        @onTertiaryBtnClick="downloadPem"
        @closeModal="closeEditModal"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name-edit"
              variant="tertiary"
              class="certificate-name-edit-input"
              data-qa="certificate-name-edit-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="selectedCertificate.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!editCertificateNameError || !!editCertificateDuplicateError"
              :error-message-text="editCertificateNameError || editCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name-edit" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name-edit"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="connected-server-groups"
              class="connected-server-groups"
              data-qa="connected-server-groups-input"
              :label="$t('certificate.connectedServerGroups')"
              :inputValue="connectedServerGroups"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-subject"
              class="certificate-subject"
              data-qa="certificate-subject-input"
              :label="$t('certificate.certificateSubject')"
              :inputValue="selectedCertificate.subject"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-issuer"
              class="certificate-issuer"
              data-qa="certificate-issuer-input"
              :label="$t('certificate.issuer')"
              :inputValue="selectedCertificate.issuer"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="san"
              class="certificate-san"
              data-qa="san-input"
              :label="$t('certificate.san')"
              :inputValue="selectedCertificate.san.toString()"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              :inputValue="selectedCertificate['cert_body']"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
              disabled
            />
          </div>
          <certificates-tabs
            :selected-items="selectedApps"
            :all-items="serverGroups"
            side="clientCA"
            :assigned-certificates="assignedCertificates"
            :selected-certificate="selectedCertificate"
            :detachActionText="$t('certificate.clientCaCertificate.detachWarning')"
            @update-selected-apps="updateSelectedApps"
            @update-replace-certificate="updateReplaceCertificate"
            ref="certificatesTabsRef"
          >
          </certificates-tabs>
        </template>
      </rbz-modal-dialog>
    </div>
    <certificates-list-delete-modal
      :selected-certificate="selectedCertificate"
      side="clientCa"
      :attached-apps="attachedApps"
      :entity-name="entityName"
      @delete-certificate="deleteCertificate"
      ref="certificatesDeleteModalRef"
    >
    </certificates-list-delete-modal>
  </template>
  <rbz-loader :loading="!!isLoading" :data-exists="certificates !== null" height="100%" />
</template>
<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { ClientCaCertificate, GenericObject, ServerGroup } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useBranchesStore } from '@/store/BranchesStore';
import { mapStores } from 'pinia';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import Utils from 'ui-component-library/helpers/Utils';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzLoader from '@/components/RbzLoader.vue';
import SystemNotice from 'ui-component-library/rbz/components/SystemNotice.vue';
import CertificatesListDeleteModal from '@/components/CertificatesListDeleteModal.vue';
import CertificatesTabs from '@/components/CertificatesTabs.vue';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import IconUpload from 'ui-component-library/assets/icons/IconUpload.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';

export default defineComponent({
  name: 'CertificatesListClientCaTab',
  components: {
    IconInfoSmall,
    IconUpload,
    CertificatesTabs,
    CertificatesListDeleteModal,
    SystemNotice,
    RbzLoader,
    LayoutDividerLine,
    RbzFormInput,
    RbzFormRadio,
    RbzFormTextarea,
    RbzModalDialog,
    RbzTable,
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Certificates') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadData();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
    assignedApps: {
      handler: function (val) {
        this.selectedApps = _.cloneDeep(val);
      },
      deep: true,
      immediate: true,
    },
  },
  data() {
    const defaultNewCertificateData = {
      isManualInput: 'manual',
      file: null,
      certificate: '',
      extension: null as 'pem' | 'der',
      name: '',
    };
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('certificate.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
          autoFocus: true,
        },
        {
          id: 'expiration-date',
          title: this.$t('certificate.expirationDate'),
          fieldNames: ['exp_date'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item: ClientCaCertificate) => {
            const dateUnix = Number(new Date(`${item['exp_date']}T00:00:00.000Z`));
            const currentDateUnix = Number(new Date());
            const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;
            return dateUnix - currentDateUnix < ONE_MONTH ? 'has-text-danger' : '';
          },
        },
        {
          id: 'linked-to',
          title: this.$t('certificate.linkedTo'),
          displayFunction: (item: ClientCaCertificate) => {
            const matchingServerGroups: ServerGroup[] = _.filter(
              this.serverGroups,
              (serverGroup: ServerGroup) => {
                return serverGroup.client_certificate === item.id;
              }
            );
            return matchingServerGroups.length
              ? _.map(matchingServerGroups, 'name').join('\n')
              : '';
          },
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'san',
          title: this.$t('certificate.san'),
          fieldNames: ['san'],
          displayFunction: (item) => {
            return item.san?.join('\n') || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-280px',
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
        {
          id: 'issuer',
          title: this.$t('certificate.issuer'),
          fieldNames: ['issuer'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      certificates: null as ClientCaCertificate[],
      serverGroups: null as ServerGroup[],
      pageLoadingCounter: null,
      showNewModal: false,
      showEditModal: false,
      defaultNewCertificateData: _.cloneDeep(defaultNewCertificateData),
      newCertificateData: _.cloneDeep(defaultNewCertificateData),
      isDownloadLoading: false,
      selectedCertificate: null as ClientCaCertificate,
      selectedApps: [] as ServerGroup[],
      attachedApps: [] as string[],
      replaceCertificate: null as ClientCaCertificate['id'],
      newCertificateId: '',
      isCertDitached: false,
      isClientToV5ClientCaCertEnabled: false as boolean,
      isCertDownloadEnabled: false as boolean,
      newCertificateNameError: '',
      editCertificateNameError: '',
      newCertificateDuplicateError: '',
      editCertificateDuplicateError: '',
      certificatesInUse: [] as { name: string; id: string; side: string }[],
    };
  },
  computed: {
    Utils() {
      return Utils;
    },
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore),

    entityName() {
      return {
        plural: this.$t('certificate.clientCaCertificate.plural'),
        singular: this.$t('certificate.clientCaCertificate.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    serverGroupsByCertNameMap(): GenericObject {
      const returnValue: { [key: string]: string[] } = {};
      this.serverGroups.forEach((serverGroup: ServerGroup) => {
        const certId = serverGroup.client_certificate;
        if (certId) {
          if (!returnValue[certId]) {
            returnValue[certId] = [];
          }
          returnValue[certId].push(serverGroup.name);
        }
      });
      return returnValue;
    },

    connectedServerGroups(): string {
      const serverGroups = this.serverGroupsByCertNameMap[this.selectedCertificate.id];
      return serverGroups?.join(', ') || '';
    },

    assignedApps(): ServerGroup[] {
      return (
        _.filter(this.serverGroups, (serverGroup: ServerGroup) => {
          return serverGroup?.client_certificate === this.selectedCertificate?.id;
        }) || []
      );
    },

    assignedCertificates(): ClientCaCertificate[] {
      const serverGroupsAssignedCertificates: ClientCaCertificate[] = [];
      _.forEach(this.serverGroups, (serverGroup: ServerGroup) => {
        const certificate = _.find(this.certificates, (certificate) => {
          return certificate.id === serverGroup.client_certificate;
        });
        if (certificate) {
          serverGroupsAssignedCertificates.push(certificate);
        }
      });
      return _.union(serverGroupsAssignedCertificates);
    },
  },
  methods: {
    validateCertificateName(name: string): boolean {
      const nameRegex = /^[a-zA-Z0-9_-]*$/;
      if (name && !nameRegex.test(name)) {
        return false;
      }
      return true;
    },

    getCertificateTypeByName(name: string, excludeId?: string): string {
      if (!name || !this.certificatesInUse) return '';
      const certType = this.certificatesInUse
        .filter((cert) => cert.id !== excludeId)
        .find((cert) => cert.name?.toLowerCase() === name.toLowerCase())?.side;
      return certType || '';
    },

    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadData() {
      this.setPageLoadingStatus(true);
      const certificatesPromise = this.loadCertificates();
      const loadNamspacePromise = this.loadNamespaces();
      const serverGroupsPromise = this.loadServerGroups();
      const isCertDownloadEnabledPromise = this.loadIsCertDownloadEnabled();
      await Promise.all([
        certificatesPromise,
        loadNamspacePromise,
        serverGroupsPromise,
        isCertDownloadEnabledPromise,
      ]);
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/certificates`;
      const certificatesResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.certificates = certificatesResponse?.data?.items || [];
      this.certificatesInUse = this.certificates.map((certificate) => {
        return { name: certificate.name, id: certificate.id, side: certificate.side };
      });
      this.certificates = _.filter(this.certificates, (certificate: ClientCaCertificate) => {
        return certificate.side === 'clientCA';
      });
      this.setPageLoadingStatus(false);
    },

    async loadNamespaces() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `db/system`,
      });
      const featureToggle = response?.data?.['feature-toggle'];
      if (featureToggle) {
        this.isClientToV5ClientCaCertEnabled =
          !!featureToggle['ssl-client-to-v5-client-ca-certificate'];
      }
      this.setPageLoadingStatus(false);
    },

    async loadServerGroups() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/server-groups`;
      const serverGroupsResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.serverGroups = serverGroupsResponse?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadIsCertDownloadEnabled() {
      this.setPageLoadingStatus(true);
      const url = 'tools/versions';
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      if (response?.data) {
        this.isCertDownloadEnabled = response.data.cert_download_enabled;
      }
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(certificate: ClientCaCertificate) {
      return this.isCertificateReferenced(certificate);
    },

    isCertificateReferenced(certificate: ClientCaCertificate): boolean {
      return this.serverGroups.some(
        (serverGroup: ServerGroup) => serverGroup.client_certificate === certificate.id
      );
    },

    deleteTooltip(certificate: ClientCaCertificate): string {
      if (this.isCertificateReferenced(certificate)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    // New certificate modal
    openNewModal() {
      this.showNewModal = true;
    },

    closeNewModal() {
      this.showNewModal = false;
      this.newCertificateData = _.cloneDeep(this.defaultNewCertificateData);
      this.newCertificateNameError = '';
      this.newCertificateDuplicateError = '';
    },

    newModalTypeClear(type: string) {
      this.newCertificateData = _.cloneDeep(this.defaultNewCertificateData);
      this.newCertificateData.isManualInput = type;
    },

    loadCertFile(event: Event) {
      const target = event.target as HTMLInputElement;
      const file: File = target.files[0];
      const extension = file?.name?.split('.')?.pop();
      if (extension?.toLowerCase() === 'pem' || extension?.toLowerCase() === 'der') {
        this.newCertificateData.file = file;
        this.newCertificateData.certificate = '';
        this.newCertificateData.extension = extension?.toLowerCase();
      } else {
        target.value = null;
      }
    },

    async extractFile() {
      if (this.newCertificateData.extension) {
        const formData = new FormData();
        formData.append(
          'fileName',
          this.newCertificateData.file,
          this.newCertificateData.file.name
        );
        const failureMessage = this.$t('certificate.extractFailure', {
          type: this.newCertificateData.extension === 'pem' ? 'PEM' : 'DER',
        });
        const url = 'tools/certificates/extract' + this.newCertificateData.extension;
        const response = await RequestsUtils.sendRequest({
          methodName: 'POST',
          url,
          data: formData,
          config: { headers: { 'Content-Type': 'multipart/form-data' } },
          failureMessage,
        });
        if (response?.data) {
          this.newCertificateData.certificate = response.data['certificate_body'] || '';
        }
      }
    },

    newCertificate(): ClientCaCertificate {
      const factory = DatasetsUtils.newEntityFactory['client-ca-certificates'];
      return factory && factory();
    },

    async addNewCertificate() {
      const name = this.newCertificateData.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.newCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.newCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name);
      if (certType !== '') {
        this.newCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.newCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const certificateToAdd = this.newCertificate();
      const trimmedName = (this.newCertificateData.name || '').trim();
      if (trimmedName !== '') {
        certificateToAdd.name = trimmedName;
      }
      certificateToAdd['cert_body'] = this.newCertificateData.certificate;
      const successMessage = this.$t('common.operations.new.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url: `conf/${this.selectedBranch}/certificates/${certificateToAdd.id}`,
        data: certificateToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.newCertificateId = certificateToAdd.id;
      }
      this.closeNewModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    // Edit certificate modal
    openEditModal(certificate: ClientCaCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editCertificateNameError = '';
      this.editCertificateDuplicateError = '';
    },

    updateSelectedApps(newAppsIds: ServerGroup['id'][]) {
      this.selectedApps = _.filter(this.serverGroups, (serverGroup) => {
        return newAppsIds.includes(serverGroup.id);
      });
    },

    updateReplaceCertificate(id: ClientCaCertificate['id'][]) {
      this.replaceCertificate = id;
    },

    async editCertificate() {
      const name = this.selectedCertificate.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.editCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.editCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name, this.selectedCertificate.id);
      if (certType !== '') {
        this.editCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.editCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const serverGroupsToRemove = _.differenceBy(this.assignedApps, this.selectedApps, 'id');
      const serverGroupsToAdd = _.differenceBy(this.selectedApps, this.assignedApps, 'id');
      const methodName = 'PUT';
      for (const serverGroup of serverGroupsToRemove) {
        const url = `conf/${this.selectedBranch}/server-groups/${serverGroup.id}`;
        serverGroup.client_certificate = '';
        serverGroup.client_certificate_mode = 'off';
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: serverGroup,
        });
      }
      for (const serverGroup of serverGroupsToAdd) {
        const url = `conf/${this.selectedBranch}/server-groups/${serverGroup.id}`;
        serverGroup['client_certificate'] = this.selectedCertificate.id;
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: serverGroup,
        });
      }
      const nameParam = (this.selectedCertificate.name || '').trim();
      let urlArgs = nameParam ? `?name=${encodeURIComponent(nameParam)}` : '?';
      if (this.replaceCertificate) {
        urlArgs = `${urlArgs}&replace_cert_id=${this.replaceCertificate}`;
        this.replaceCertificate = null;
      }
      const url = `conf/${this.selectedBranch}/certificates/${this.selectedCertificate.id}${urlArgs}`;
      const successMessage = this.$t('certificate.clientCaCertificate.editModal.attachSuccess');
      const data = this.selectedCertificate;
      await RequestsUtils.sendRequest({
        methodName: methodName,
        url,
        data,
        successMessage,
      });
      this.closeEditModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    async downloadPem() {
      this.isDownloadLoading = true;
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/certificates/${this.selectedCertificate.id}/pem`,
        config: {
          responseType: 'blob',
        },
      });
      if (response) {
        Utils.downloadFile('certificate', 'pem', response.data);
      } else {
        Utils.toast({
          message: this.$t('certificate.downloadFailure', { type: 'PEM' }),
          type: 'is-danger',
        });
      }
      this.isDownloadLoading = false;
    },

    // Delete certificate modal
    openDeleteModal(certificate: ClientCaCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.attachedApps = this.serverGroupsByCertNameMap[certificate.id] || [];
      this.$refs.certificatesDeleteModalRef.openDeleteModal();
    },

    async deleteCertificate() {
      this.setPageLoadingStatus(true);
      await this.$refs.certificatesDeleteModalRef.deleteCertificate();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },
  },
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.certificate-type-wrapper {
  display: flex;
  gap: 16px;
}

:deep(.rbz-table .data-row) {
  &.highlight-row {
    background-color: map.get(colors.$colors, 'ice-blue');
  }
}

:deep(.rbz-table .multi-line) {
  -webkit-line-clamp: 10;
}

.certificate-details-divider {
  margin: 20px 0;
}

.new-certificate-modal-notice-wrapper {
  margin-bottom: 16px;
}
</style>
